import type { ReactNode } from "react";
import { useState } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";

interface EditPageLayoutProps {
  title: string;
  description?: string;
  isLoading: boolean;
  children: ReactNode;
  formId: string;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  cancelLabel?: string;
  onSave: () => void;
  saveLabel?: string;
  savingLabel?: string;
  isSaving: boolean;
  onDelete?: () => void;
  deleteLabel?: string;
  deletingLabel?: string;
  isDeleting?: boolean;
  deleteDialogTitle?: string;
  deleteDialogDescription?: string;
  loadingContent?: ReactNode;
  entityExists?: boolean;
}

export function EditPageLayout({
  title,
  description,
  isLoading,
  children,
  formId,
  onSubmit: _onSubmit,
  onCancel,
  cancelLabel = "Cancel",
  onSave,
  saveLabel = "Save",
  savingLabel = "Saving...",
  isSaving,
  onDelete,
  deleteLabel = "Delete",
  deletingLabel = "Deleting...",
  isDeleting = false,
  deleteDialogTitle,
  deleteDialogDescription,
  loadingContent,
  entityExists = true,
}: EditPageLayoutProps): JSX.Element {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDeleteClick = (): void => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (onDelete) {
      await onDelete();
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <div className="space-y-4 p-3 sm:p-4 md:p-8">
      <PageHeader title={title} description={description} onBack={onCancel} />

      <Card className="overflow-hidden">
        <CardContent className="space-y-6 px-4 py-6 sm:space-y-10 sm:px-8 sm:py-10">
          {isLoading ? (
            loadingContent || (
              <div className="flex justify-center py-8">
                <LoadingLogo size="sm" />
              </div>
            )
          ) : (
            children
          )}
        </CardContent>
        {!isLoading && entityExists && (
          <CardFooter className="border-t px-4 py-4 sm:px-8 sm:py-6">
            <div className="flex w-full items-center justify-between gap-3">
              {onDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteClick}
                  disabled={isSaving || isDeleting}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {deleteLabel}
                </Button>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSaving || isDeleting}
                >
                  {cancelLabel}
                </Button>
                <Button
                  type="submit"
                  form={formId}
                  onClick={onSave}
                  disabled={isSaving || isDeleting}
                >
                  {isSaving ? savingLabel : saveLabel}
                </Button>
              </div>
            </div>
          </CardFooter>
        )}
      </Card>

      {onDelete && deleteDialogTitle && deleteDialogDescription && (
        <ConfirmationDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          title={deleteDialogTitle}
          description={deleteDialogDescription}
          confirmText={isDeleting ? deletingLabel : deleteLabel}
          cancelText={cancelLabel}
          variant="destructive"
          isLoading={isDeleting}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
